import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import CNESSLogo from "./CNESSLogo";

const PublicNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/" },
    { 
      name: "Certification",
      path: "/certification",
      dropdown: [
        { name: "Overview", path: "/certification" },
        { name: "Six Pillars Framework", path: "/framework" },
        { name: "Certification Tiers", path: "/certification-tiers" },
        { name: "Why CNESS Works", path: "/why-cness" },
      ]
    },
    { 
      name: "Partners",
      path: "/partner-program",
      dropdown: [
        { name: "Partner Program", path: "/partner-program" },
        { name: "Become a Partner", path: "/partner-application" },
        { name: "Partner Resources", path: "/partner-resources" },
      ]
    },
    { 
      name: "Resources",
      path: "/resources",
      dropdown: [
        { name: "Success Stories", path: "/success-stories" },
        { name: "Community Hub", path: "/community" },
        { name: "Gallery & Media", path: "/gallery" },
        { name: "Blog", path: "https://cnessofficial.blogspot.com/?m=1", external: true },
        { name: "FAQs", path: "/faqs" },
      ]
    },
    { name: "Directory", path: "/directory" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleDropdownItemClick = (path) => {
    setActiveDropdown(null);
    navigate(path);
  };

  // CNESS Sun Logo Component
  const CNESSLogoBlock = () => (
    <div className="flex items-center">
      <CNESSLogo variant="full" />
    </div>
  );

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white"
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="public-navbar-logo">
            <CNESSLogoBlock />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div 
                key={link.name}
                className="relative"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                    isActive(link.path)
                      ? "text-[#1E3A5F] bg-[#1E3A5F]/5"
                      : "text-gray-600 hover:text-[#1E3A5F] hover:bg-gray-50"
                  }`}
                >
                  {link.name}
                  {link.dropdown && (
                    <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                  )}
                </Link>
                
                {/* Dropdown Menu - Fixed clickability */}
                {link.dropdown && activeDropdown === link.name && (
                  <div 
                    className="absolute top-full left-0 pt-2 w-56"
                    onMouseEnter={() => setActiveDropdown(link.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                      {link.dropdown.map((item) => (
                        item.external ? (
                          <a
                            key={item.name}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:text-[#1E3A5F] hover:bg-[#F8FAFC] transition-colors flex items-center justify-between"
                          >
                            <span>{item.name}</span>
                            <span className="text-xs text-[#FDB813]">↗</span>
                          </a>
                        ) : (
                          <button
                            key={item.name}
                            onClick={() => handleDropdownItemClick(item.path)}
                            className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:text-[#1E3A5F] hover:bg-[#F8FAFC] transition-colors"
                          >
                            {item.name}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Button 
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-[#1E3A5F] hover:bg-[#1E3A5F]/5 font-medium"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-6 rounded-xl font-medium"
            >
              Get Certified
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-[#1E3A5F]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1E3A5F]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                      isActive(link.path)
                        ? "text-[#1E3A5F] bg-[#1E3A5F]/5"
                        : "text-gray-600 hover:text-[#1E3A5F] hover:bg-gray-50"
                    }`}
                    onClick={() => !link.dropdown && setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="pl-4 space-y-1 mt-1">
                      {link.dropdown.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => { setIsOpen(false); navigate(item.path); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-500 hover:text-[#1E3A5F]"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
              <Button 
                variant="outline"
                onClick={() => { navigate('/login'); setIsOpen(false); }}
                className="w-full justify-center border-[#1E3A5F] text-[#1E3A5F]"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => { navigate('/signup'); setIsOpen(false); }}
                className="w-full justify-center bg-[#1E3A5F] hover:bg-[#152a45] text-white"
              >
                Get Certified
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default PublicNavbar;
